import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  catchError,
  concatMap,
  from,
  of,
  retry,
  Subscription,
  toArray,
} from 'rxjs';
import { SyncStatus } from 'src/app/core/interfaces/offline/sync-status';
import { ApiService } from 'src/app/core/services/api/api.service';
import { IdbService } from 'src/app/core/services/idb/idb.service';
import { OfflineService } from 'src/app/core/services/offline/offline.service';
import { RoleService } from 'src/app/core/services/role/role.service';
import { SnackbarService } from 'src/app/core/services/snackbar/snackbar.service';
import { SyncService } from 'src/app/core/services/sync/sync.service';

@Component({
  selector: 'app-sync-dialog',
  templateUrl: './sync-dialog.component.html',
  styleUrls: ['./sync-dialog.component.scss'],
})
export class SyncDialogComponent implements OnInit {
  componentToDisplay: string;

  currentSyncStatus: SyncStatus;
  currentConnectionStatus: boolean;

  dynamicStatusText: string; // Change this based on sync and connection status
  dynamicStatusIcon: string; // Change this based on sync and connection status
  dynamicStatusColor: string; // Change this based on sync and connection status

  syncErrorMessage: string; // To display sync error messages

  syncStatusSubscription: Subscription;
  connectionStatusSubscription: Subscription;

  constructor(
    public dialogRef: MatDialogRef<SyncDialogComponent>,
    private api: ApiService,
    private idb: IdbService,
    private syncService: SyncService,
    private offline: OfflineService,
    private role: RoleService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      userRole: string; // User role to determine sync type
      initialSync: boolean; // Indicates if this is the initial sync
    }
  ) {
    dialogRef.disableClose = true;
  }

  ngOnInit(): void {
    this.syncStatusSubscription = this.syncService.currentSyncStatus.subscribe(
      (syncStatus) => {
        this.currentSyncStatus = syncStatus;
        this.assignDynamicStatusVariables();

        if (this.currentSyncStatus.syncStatus === 'Synced Up') {
          setTimeout(() => {
            console.log(
              'Sync to server completed successfully. Delaying for 2 seconds before triggering sync down from server.'
            );
            // If sync is successful, trigger sync down from server
            this.triggerSyncDownFromServer();
          }, 2000);
        }
      }
    );
    this.connectionStatusSubscription =
      this.offline.currentConnectionStatus.subscribe((connectionStatus) => {
        this.currentConnectionStatus = connectionStatus;
        this.assignDynamicStatusVariables();
      });

    if (this.data.initialSync) {
      // If this is the initial sync, we can directly trigger the sync to server
      this.componentToDisplay = 'initialSync';
      this.triggerSyncDownFromServer();
    } else {
      // If this is not the initial sync, we can show the pre-sync component
      this.componentToDisplay = 'syncDashboard';
    }
  }

  triggerSyncToServer() {
    console.log('Triggering sync to server...');
    this.componentToDisplay = 'syncInProgress';

    this.api.getServerStatus().subscribe({
      next: (status) => {
        let unfinishedUpsellEvents = false;

        this.idb.getAllUpsellCalendarEvents().subscribe({
          next: (upsellCalendarEvents) => {
            if (upsellCalendarEvents.length > 0) {
              unfinishedUpsellEvents = upsellCalendarEvents.some(
                (event) => event.status === 0
              );
            }

            if (unfinishedUpsellEvents) {
              this.showUnfinishedUpsellMessage();
              return;
            }

            switch (this.data.userRole) {
              case 'Estimator':
                this.syncService.salesSyncDataToServer().subscribe({
                  next: () => {
                    // Optional: show sync step message
                  },
                  error: (error) => {
                    console.error('Sync to server failed:', error);
                    this.handleSyncError(
                      'There was an error uploading your data.'
                    );
                  },
                  complete: () => {
                    this.syncAttachments().subscribe({
                      next: () => {
                        // Optional: show sync step message
                      },
                      error: (error) => {
                        console.error('Syncing attachments failed:', error);
                        this.handleSyncError(
                          'There was an error uploading your attachments.'
                        );
                      },
                      complete: () => {
                        this.syncService
                          .updateSyncStatus({
                            syncId: 1,
                            syncStatus: 'Synced Up',
                            lastSynced: new Date(),
                          })
                          .subscribe({
                            error: (err) => {
                              console.error(
                                'Failed to update sync status',
                                err
                              );
                              this.handleSyncError(
                                'There was an unexpected error.'
                              );
                            },
                          });
                      },
                    });
                  },
                });
                break;
              case 'Foreman':
                console.log('Syncing Foreman data to server...');
                this.syncService.prodSyncDataToServer().subscribe({
                  next: () => {
                    // Optional: show sync step message
                  },
                  error: (error) => {
                    console.error('Sync to server failed:', error);
                    this.handleSyncError(
                      'There was an error uploading your data.'
                    );
                  },
                  complete: () => {
                    this.syncAttachments().subscribe({
                      next: () => {
                        // Optional: show sync step message
                      },
                      error: (error) => {
                        console.error('Syncing attachments failed:', error);
                        this.handleSyncError(
                          'There was an error uploading your attachments.'
                        );
                      },
                      complete: () => {
                        this.syncService
                          .updateSyncStatus({
                            syncId: 1,
                            syncStatus: 'Synced Up',
                            lastSynced: new Date(),
                          })
                          .subscribe({
                            error: (err) => {
                              console.error(
                                'Failed to update sync status',
                                err
                              );
                              this.handleSyncError(
                                'There was an unexpected error.'
                              );
                            },
                          });
                      },
                    });
                  },
                });
                break;
              case 'Service Tech':
                this.syncService.serviceSyncDataToServer().subscribe({
                  next: () => {
                    // Optional: show sync step message
                  },
                  error: (error) => {
                    console.error('Sync to server failed:', error);
                    this.handleSyncError(
                      'There was an error uploading your data.'
                    );
                  },
                  complete: () => {
                    this.syncAttachments().subscribe({
                      next: () => {
                        // Optional: show sync step message
                      },
                      error: (error) => {
                        console.error('Syncing attachments failed:', error);
                        this.handleSyncError(
                          'There was an error uploading your attachments.'
                        );
                      },
                      complete: () => {
                        this.syncService
                          .updateSyncStatus({
                            syncId: 1,
                            syncStatus: 'Synced Up',
                            lastSynced: new Date(),
                          })
                          .subscribe({
                            error: (err) => {
                              console.error(
                                'Failed to update sync status',
                                err
                              );
                              this.handleSyncError(
                                'There was an unexpected error.'
                              );
                            },
                          });
                      },
                    });
                  },
                });
                break;

              // Add other roles here
            }
          },
          error: (err) => {
            console.error('Failed to get upsell calendar events:', err);
            this.handleSyncError('Unable to read local data.');
          },
        });
      },
      error: (err) => {
        console.error('Server status check failed:', err);
        this.handleSyncError('Could not connect to the server.');
      },
    });
  }

  triggerSyncDownFromServer() {
    switch (this.data.userRole) {
      case 'Estimator':
        this.syncService.salesSyncDownAfterUpload().subscribe({
          next: (data) => {},
          error: (error) => {
            console.error('Sync failed', error);
            this.handleSyncError(
              'There was an error downloading your data from the server.'
            );
          },
          complete: () => {
            this.componentToDisplay = 'syncComplete';
          },
        });
        break;
      case 'Foreman':
        this.syncService.prodSyncDownAfterUpload().subscribe({
          next: (data) => {},
          error: (error) => {
            console.error('Sync failed', error);
            this.handleSyncError(
              'There was an error downloading your data from the server.'
            );
          },
          complete: () => {
            this.componentToDisplay = 'syncComplete';
          },
        });
        break;
      case 'Service Tech':
        this.syncService.serviceSyncDownAfterUpload().subscribe({
          next: (data) => {},
          error: (error) => {
            console.error('Sync failed', error);
            this.handleSyncError(
              'There was an error downloading your data from the server.'
            );
          },
          complete: () => {
            this.componentToDisplay = 'syncComplete';
          },
        });
        break;
    }
  }

  syncAttachments() {
    const fileAttachments$ = this.syncService.getPWAAttachmentsAsFiles();

    return fileAttachments$.pipe(
      concatMap((files) => from(files)),
      concatMap((group) => from(group)),
      concatMap((file) => {
        const pieces = file.name.split('!');
        const jobAddressId = pieces[0];
        const customerId = pieces[1];
        const functionId = pieces[2];
        const functionName = pieces[3];
        const attachmentLabel = pieces[4];
        const formData = new FormData();
        formData.append('file', file);
        const fileUpload = this.api
          .saveFiles(
            formData,
            Number(customerId),
            Number(jobAddressId),
            functionName,
            functionId,
            attachmentLabel
          )
          .pipe(
            retry(1),
            catchError((error) => {
              console.error(
                `Failed to upload file: ${file.name}, error: ${error}`
              );
              return of('error');
            })
          );
        return fileUpload;
      }),
      toArray()
    );
  }

  assignDynamicStatusVariables() {
    if (this.currentConnectionStatus) {
      // Connected, set based on sync status
      switch (this.currentSyncStatus.syncStatus) {
        case 'Syncing Up':
          this.dynamicStatusText =
            'Sync in Progress: Syncing data with server...';
          this.dynamicStatusIcon = 'sync';
          this.dynamicStatusColor = 'primary';
          break;
        case 'Synced Up':
          this.dynamicStatusText =
            'Sync in Progress: Data synced up successfully!';
          this.dynamicStatusIcon = 'check_circle';
          this.dynamicStatusColor = 'accent';
          break;
        case 'Syncing Down':
          this.dynamicStatusText =
            'Sync in Progress: Downloading data from server...';
          this.dynamicStatusIcon = 'sync';
          this.dynamicStatusColor = 'primary';
          break;
        case 'Synced Down':
          this.dynamicStatusText =
            "You're ready to sync. Ensure you have a reliable connection before syncing.";
          this.dynamicStatusIcon = 'check_circle';
          this.dynamicStatusColor = 'accent';
          break;
        case 'Syncing Attachments':
          this.dynamicStatusText = 'Sync in Progress: Syncing attachments...';
          this.dynamicStatusIcon = 'attach_file';
          this.dynamicStatusColor = 'primary';
          break;
        case 'Sync Failed':
          this.dynamicStatusText = 'Sync failed. Please try again.';
          this.dynamicStatusIcon = 'error';
          this.dynamicStatusColor = 'warn';
          break;
      }
    } else {
      // Not connected message
      this.dynamicStatusText =
        'No internet connection. Find a reliable connection and try again.';
      this.dynamicStatusIcon = 'wifi_off';
      this.dynamicStatusColor = 'warn';
    }
  }

  showUnfinishedUpsellMessage() {
    this.componentToDisplay = 'unfinishedUpsell';
  }

  handleSyncError(message: string) {
    this.componentToDisplay = 'syncError'; // Switch to your error template
    this.syncErrorMessage = message; // You can bind this in the template
  }

  retryFailedSync() {
    // Restart the sync process
    this.triggerSyncToServer();
    // Should probably differentiate between retrying sync to server and sync down from server
  }

  closeDialog() {
    this.dialogRef.close();
  }

  ngOnDestroy() {
    this.syncStatusSubscription.unsubscribe();
    this.connectionStatusSubscription.unsubscribe();
  }
}
